// TODO: Need to determine the correct values
import {
  ERC725YDataKeys as ERC725YDataKeys_VX,
  INTERFACE_IDS as INTERFACE_IDS_VX,
} from '@lukso/lsp-smart-contracts'
import UniversalProfile_vX from '@lukso/lsp-smart-contracts/artifacts/UniversalProfile.json'
import ERC725 from '@erc725/erc725.js'
import LSP3ProfileMetadata from '@erc725/erc725.js/schemas/LSP3ProfileMetadata.json'
import assert from 'assert'
import { ResultOrError } from '@lukso/controllerapps-sharedlogic-ddd-core'
import {
  HexNumber,
  Address,
  ChainID,
} from '@lukso/controllerapps-sharedlogic-primitives'

import { UniversalProfileContract } from '../../../domain/contracts/UniversalProfileContract'
import { assertViemAbiItemArray } from '../../typeguards/AbiItemTypeguard'
import { ProfileContractCallError } from '@/business-logic/profile/domain/errors/ProfileContractCallError'
import * as AddressPermissionsLengthMap from './maps/AddressPermissionsLengthMap'
import * as ControllerAddressMap from './maps/ControllerAddressMap'
import * as PermissionsMap from './maps/PermissionsMap'
import * as ProfileNameMap from './maps/ProfileNameMap'
import { ProfileAddress } from '@/business-logic/profile/domain/ProfileAddress'
import { ControllerAddress } from '@/business-logic/profile/domain/ControllerAddress'
import { Permissions } from '@/business-logic/profile/domain/Permissions'
import { ViemEIP165Contract } from '../ViemEIP165Contract/ViemEIP165Contract'
import { InterfaceID } from '@/business-logic/profile/domain/InterfaceID'
import { ProfileName } from '@/business-logic/profile/domain/ProfileName'
import { AddressPermissionsIndex } from '@/business-logic/profile/domain/contracts/AddressPermissionsIndex'
import { AddressPermissionsLength } from '@/business-logic/profile/domain/contracts/AddressPermissionsLength'
import { ViemHelper } from '../ViemHelper/ViemHelper'
import { assertString } from '@/shared/validators'
// TODO: Need to determine the correct values of all below for all UP versions that we support. We should also document
// these values in the README, not just for profiles, but for all the LSPs we support.
const interfaceID_VX = InterfaceID.create({
  interfaceIDValue: HexNumber.create({
    hexNumberValue: INTERFACE_IDS_VX.LSP0ERC725Account,
  }),
})

const ipfsAPI = 'https://api.universalprofile.cloud/ipfs/' //must end with a slash

export class ViemUniversalProfileContract implements UniversalProfileContract {
  constructor(private readonly eip165Contract: ViemEIP165Contract) {}

  private getContract(profileAddress: ProfileAddress, chainID: ChainID) {
    const viem = ViemHelper.create(chainID)
    const address = profileAddress.getValue()
    assertViemAbiItemArray(UniversalProfile_vX.abi)
    return viem.createContract(address, UniversalProfile_vX.abi)
  }

  public async isUniversalProfile(
    address: Address,
    chainID: ChainID
  ): Promise<ResultOrError<boolean, ProfileContractCallError>> {
    return this.eip165Contract.supportsInterface(
      address,
      interfaceID_VX,
      chainID
    )
  }

  private resolveIpfsURL(ipfsURL: string): string {
    const ipfsPrefix = 'ipfs://'
    assert(
      ipfsURL.startsWith(ipfsPrefix),
      'Invalid IPFS URI: Does not start with ipfs://'
    )
    const identifier = ipfsURL.slice(ipfsPrefix.length)
    assert(identifier.length > 0, 'Invalid IPFS URI: No identifier provided')
    return `${ipfsAPI}${identifier}`
  }

  public async getProfileName(
    profileAddress: ProfileAddress,
    chainID: ChainID
  ): Promise<ResultOrError<ProfileName | undefined, ProfileContractCallError>> {
    try {
      const contract = this.getContract(profileAddress, chainID)

      const lsp3ProfileDataKey = ERC725YDataKeys_VX.LSP3.LSP3Profile

      const value = await contract.read.getData([lsp3ProfileDataKey])

      assertString(
        value,
        `Expected string while getting data while getting profile name, but got ${typeof value}`
      )

      const data = ERC725.decodeData(
        [{ keyName: lsp3ProfileDataKey, value }],
        LSP3ProfileMetadata
      )

      const ipfsURL =
        data &&
        typeof data === 'object' &&
        0 in data &&
        typeof data[0] === 'object' &&
        data[0] &&
        'value' in data[0] &&
        typeof data[0].value === 'object' &&
        data[0].value &&
        'url' in data[0].value &&
        typeof data[0].value.url === 'string'
          ? data[0].value.url
          : undefined

      if (!ipfsURL) {
        return ResultOrError.success()
      }

      const resolvedIpfsURL = this.resolveIpfsURL(ipfsURL)

      const response = await fetch(resolvedIpfsURL)
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      const lsp3ProfileValue = await response.json()

      const profileName = ProfileNameMap.mapDTOToDomain(lsp3ProfileValue)

      return ResultOrError.success(profileName)
    } catch (error) {
      let message = `Error getting the name for profile ${profileAddress.getChecksumValue()}`
      if (error instanceof Error) {
        message = `${message}: ${error.message}`
      }
      return ResultOrError.error(
        new ProfileContractCallError('UniversalProfile', message)
      )
    }
  }

  public async getAddressPermissionsArrayLength(
    profileAddress: ProfileAddress,
    chainID: ChainID
  ): Promise<
    ResultOrError<AddressPermissionsLength, ProfileContractCallError>
  > {
    try {
      const [lengthDataKey] = Object.values(
        ERC725YDataKeys_VX.LSP6['AddressPermissions[]']
      )

      const contract = this.getContract(profileAddress, chainID)

      const controllersLengthValue = await contract.read.getData([
        lengthDataKey,
      ])

      const result = AddressPermissionsLengthMap.mapDTOToDomain(
        controllersLengthValue
      )

      return ResultOrError.success(result)
    } catch (error) {
      let message = `Error getting the length of the AddressPermissions[] array for profile ${profileAddress.getChecksumValue()}`
      if (error instanceof Error) {
        message = `${message}: ${error.message}`
      }
      return ResultOrError.error(
        new ProfileContractCallError('UniversalProfile', message)
      )
    }
  }

  public async getControllerAddress(
    addressPermissionsIndex: AddressPermissionsIndex,
    profileAddress: ProfileAddress,
    chainID: ChainID
  ): Promise<ResultOrError<ControllerAddress, ProfileContractCallError>> {
    try {
      const [, indexDataKey] = Object.values(
        ERC725YDataKeys_VX.LSP6['AddressPermissions[]']
      )

      const contract = this.getContract(profileAddress, chainID)

      const controllerAddressDataKey = `${indexDataKey}${addressPermissionsIndex.getPadded32Value()}`

      const controllerAddressValue = await contract.read.getData([
        controllerAddressDataKey,
      ])

      const controllerAddress = ControllerAddressMap.mapDTOToDomain(
        controllerAddressValue
      )

      return ResultOrError.success(controllerAddress)
    } catch (error) {
      let message = `Error getting the controller Address from the AddressPermissions[] array for profile ${profileAddress.getChecksumValue()} at index ${addressPermissionsIndex
        .getValue()
        .getDecimalValue()}`
      if (error instanceof Error) {
        message = `${message}: ${error.message}`
      }
      return ResultOrError.error(
        new ProfileContractCallError('UniversalProfile', message)
      )
    }
  }

  public async getPermissions(
    controllerAddress: ControllerAddress,
    profileAddress: ProfileAddress,
    chainID: ChainID
  ): Promise<ResultOrError<Permissions, ProfileContractCallError>> {
    try {
      const controllerAddressValue =
        ControllerAddressMap.mapDomainToDTO(controllerAddress)

      const viemContract = this.getContract(profileAddress, chainID)

      const permissionsDataKey = `${
        ERC725YDataKeys_VX.LSP6['AddressPermissions:Permissions']
      }${controllerAddressValue.slice(2)}`

      let permissionsValue = await viemContract.read.getData([
        permissionsDataKey,
      ])

      // Sometimes the node we pull data from takes a few seconds to get the data, so we need to retry
      // See: DEV-11609
      let count = 0
      while (!permissionsValue && ++count < 10) {
        await new Promise(resolve => setTimeout(resolve, 3000))
        permissionsValue = await viemContract.read.getData([permissionsDataKey])
      }

      const permissions = PermissionsMap.mapDTOToDomain(permissionsValue)

      return ResultOrError.success(permissions)
    } catch (error) {
      let message = `Error getting the controller Address from the AddressPermissions[] array for profile ${profileAddress.getChecksumValue()} with controller ${controllerAddress.getChecksumValue()}`
      if (error instanceof Error) {
        message = `${message}: ${error.message}`
      }
      return ResultOrError.error(
        new ProfileContractCallError('UniversalProfile', message)
      )
    }
  }
}
